import { Box } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector, useProjectDistributionDateRange } from '../../store/redux/hooks'
import { getMonthRangePickerDisableFlag } from '../../store/redux/reducers/pageHeaderSlice'
import DateFormat from '../../store/types/enums/DateFormat'

export type ProjectDistributionHeaderSettings = {
  shown: boolean
  disablePaginationDefaultForwardMonths?: boolean
}

const ProjectDistributionHeaderComponent = () => {
  const { t } = useTranslation()
  const { setFromDate, setToDate } = useProjectDistributionDateRange()
  const { from, to } = useAppSelector(state => state.projectDistributionDateRange)
  const disablePaginationDefaultForwardMonths = useAppSelector(getMonthRangePickerDisableFlag)
  const paginationMaxForwardMonths = 11
  const paginationDefaultForwardMonths = 1

  const [hasDateRangeError, setHasDateRangeError] = useState<boolean>(false)
  const hasDateRangeErrorRef = useRef(hasDateRangeError)
  hasDateRangeErrorRef.current = hasDateRangeError

  const fromDate = dayjs(from)
  const toDate = dayjs(to)

  useEffect(() => {
    return () => {
      if (hasDateRangeErrorRef.current) {
        setFromDate(dayjs().format(DateFormat.YearMonth))
        setToDate(dayjs().add(paginationDefaultForwardMonths, 'month').format(DateFormat.YearMonth))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (from && to) {
      const fromDateValue = dayjs(from)
      const toDateValue = dayjs(to)
      const isRangeInvalid = toDateValue.isAfter(fromDateValue.add(paginationMaxForwardMonths, 'month'))
      setHasDateRangeError(isRangeInvalid)
    }
  }, [from, to])

  const handleFromDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) {
      setFromDate('')
      return
    }

    setFromDate(date.format(DateFormat.YearMonth))

    const shouldChangeToDateByForwardMonthValue = date.isAfter(fromDate, 'month') && date.isSameOrAfter(toDate, 'month')

    if (hasDateRangeError || !disablePaginationDefaultForwardMonths || shouldChangeToDateByForwardMonthValue) {
      setToDate(date.add(paginationDefaultForwardMonths, 'month').format(DateFormat.YearMonth))
    }
  }

  const handleToDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return

    setToDate(dayjs(date).format(DateFormat.YearMonth))
  }

  const dateRangeErrorMessage = hasDateRangeError
    ? t('components.dateRangePickerWithPresets.error.definedDateRange', {
        from: 1,
        to: paginationMaxForwardMonths + 1,
      })
    : ''

  const styles = {
    svg: {
      color: 'text.mediumEmphasis',
    },
    helperText: {
      position: 'absolute',
      top: '100%',
      left: 0,
      margin: 0,
      marginTop: '4px',
      width: '100%',
    },
  }

  return (
    <Box sx={{ display: 'flex', gap: { xs: 2, md: 5 } }}>
      <DatePicker
        onChange={handleFromDateChange}
        value={fromDate}
        label={t('components.table.cell.from')}
        views={['year', 'month']}
        slotProps={{ textField: { size: 'small' } }}
        sx={styles}
      />
      <DatePicker
        onChange={handleToDateChange}
        value={toDate}
        minDate={fromDate}
        shouldDisableYear={year => year.isAfter(fromDate.add(paginationMaxForwardMonths, 'month'), 'year')}
        shouldDisableMonth={month => month.isAfter(fromDate.add(paginationMaxForwardMonths, 'month'))}
        label={t('components.table.cell.to')}
        views={['year', 'month']}
        slotProps={{
          textField: {
            size: 'small',
            error: !!dateRangeErrorMessage,
            helperText: dateRangeErrorMessage,
            FormHelperTextProps: {
              sx: styles.helperText,
            },
          },
        }}
        sx={styles}
      />
    </Box>
  )
}

export default ProjectDistributionHeaderComponent
